// src/App.tsx

import { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { db } from '@/db/db';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { WifiOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isMobileDevice } from '@/platforms/mobile/mobileDetection';
import { slugifyStore } from '@/lib/storeRealtime';

// STORES
import { useAuthStore } from '@/store/authStore';
import { useConfigStore } from '@/store/configStore';
import { useCashStore } from '@/store/cashStore';
import { SUPER_ROLES, ACCOUNTING_ROLES } from '@/config/constants';
import { useRequestPolling } from '@/hooks/useRequestPolling';

// HERRAMIENTAS DE SISTEMA
import { NiizSystemDoctor } from '@/components/ui/NiizSystemDoctor';

// TIPOS
type BusinessType = 'RETAIL' | 'RESTAURANT' | 'PROPERTY' | 'RENTAL' | 'HOSPITALITY' | 'LOGISTICS' | 'ACCOUNTING_FIRM';

// ─────────────────────────────────────────────────────────────
// 1. COMPONENTES UI & LAYOUTS
// ─────────────────────────────────────────────────────────────

const LoadingScreen = ({ message = "Cargando NIIZ..." }: { message?: string }) => (
  <div className="min-h-screen w-full flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
    <div className="relative w-24 h-24 mb-8">
      <div className="absolute inset-0 bg-indigo-600/20 rounded-full animate-ping" />
      <div className="relative bg-white dark:bg-slate-900 rounded-full shadow-xl w-full h-full flex items-center justify-center border border-slate-200 dark:border-slate-800 z-10">
        <span className="text-4xl font-black text-indigo-600 dark:text-indigo-400 select-none">N</span>
      </div>
    </div>
    <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400 animate-pulse">
      <Loader2 className="animate-spin" size={20} />
      <p className="font-medium text-lg tracking-wide">{message}</p>
    </div>
  </div>
);

const LoadingFallback = () => <LoadingScreen message="Cargando módulo..." />;

const LayoutMain = lazy(() => import('@/components/layout/LayoutMain'));
const SupplierLayout = lazy(() => import('@/components/layout/SupplierLayout').then(m => ({ default: m.SupplierLayout })));

// ─── RUTAS PÚBLICAS ───
const LandingPage = lazy(() => import('@/pages/CorporateLandingPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('@/pages/UnauthorizedPage'));
const PublicStorePage = lazy(() => import('@/pages/PublicStorePage'));
const SupplierRegistration = lazy(() => import('@/pages/SupplierRegistration').then(m => ({ default: m.SupplierRegistration })));
const MasterControlPage = lazy(() => import('@/pages/MasterControlPage'));
const NiizSegurityPage = lazy(() => import('@/pages/NiizSegurityPage'));
const SupplierPublicPage = lazy(() => import('@/pages/SupplierPublicPage'));
const WelcomeQuickAccessPage = lazy(() => import('@/pages/WelcomeQuickAccessPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'));
const SheetPage = lazy(() => import('@/pages/SheetPage'));
const EquipmentPage = lazy(() => import('@/pages/EquipmentPage'));

// ─── RETAIL ───
const RetailDashboardPage = lazy(() => import('@/platforms/retail/pages/DashboardPage'));
const RetailPOSPage = lazy(() => import('@/platforms/retail/pages/POSPage'));
const MarketplaceRetailPage = lazy(() => import('@/platforms/retail/pages/MarketplacePage'));
const SupplierOrdersPage = lazy(() => import('@/platforms/retail/pages/SupplierOrdersPage'));
const ProductsPage = lazy(() => import('@/platforms/retail/pages/ProductsPage'));
const SalesHistoryPage = lazy(() => import('@/platforms/retail/pages/SalesHistoryPage'));
const CustomersPage = lazy(() => import('@/platforms/retail/pages/CustomersPage'));
const ExpensesPage = lazy(() => import('@/platforms/retail/pages/ExpensesPage'));
const UsersPage = lazy(() => import('@/platforms/retail/pages/UsersPage'));
const ConfigPage = lazy(() => import('@/platforms/retail/pages/ConfigPage'));
const ReportsPage = lazy(() => import('@/platforms/retail/pages/ReportsPage'));
const IncentivesPage = lazy(() => import('@/platforms/retail/incentives/IncentivesPage'));
const FulfillmentDashboardPage = lazy(() => import('@/platforms/retail/pages/FulfillmentDashboardPage'));

// ─── LOGÍSTICA / DOMICILIOS / TRASTEOS ───
const LogisticsDashboardPage = lazy(() => import('@/platforms/logistics/pages/LogisticsDashboardPage'));
const PublicTrackingPage = lazy(() => import('@/platforms/logistics/pages/PublicTrackingPage'));
const CourierPortalPage = lazy(() => import('@/platforms/logistics/pages/CourierPortalPage'));
const PublicDeliveryRequestPage = lazy(() => import('@/platforms/logistics/pages/PublicDeliveryRequestPage'));

// ─── RESTAURANT ───
const TableMapPage = lazy(() => import('@/platforms/restaurant/pages/TableMapPage'));
const RestaurantOrderPage = lazy(() => import('@/platforms/restaurant/pages/RestaurantOrderPage'));
const KDSPage = lazy(() => import('@/platforms/restaurant/pages/KDSPage'));
const BarMonitor = lazy(() => import('@/platforms/restaurant/pages/BarMonitor'));

// ─── SUPPLIER ───
const SupplierDashboard = lazy(() => import('@/platforms/supplier/pages/DashboardPage'));
const SupplierCatalog = lazy(() => import('@/platforms/supplier/pages/CatalogPage'));
const SupplierConfig = lazy(() => import('@/platforms/supplier/pages/ConfigPage'));
const SupplierTemplates = lazy(() => import('@/platforms/supplier/pages/TemplatesPage'));
const SupplierAnalysisPage = lazy(() => import('@/platforms/supplier/pages/SupplierAnalysisPage'));
const SupplierSubastasPage = lazy(() => import('@/platforms/supplier/pages/SubastasPage'));
const MobileAppShell = lazy(() => import('@/platforms/mobile/MobileAppShell'));
const CertificateValidationPage = lazy(() => import('@/pages/CertificateValidationPage'));

// ─── WAREHOUSE ───
const PropertyGridPage = lazy(() => import('@/platforms/warehouse/pages/PropertyGridPage'));
const UnitsConfigPage = lazy(() => import('@/platforms/warehouse/pages/UnitsConfigPage'));

// ─── HOSPITALITY ───
const BookingPage = lazy(() => import('@/platforms/hospitality/pages/BookingPage'));
const ServicesConfigPage = lazy(() => import('@/platforms/hospitality/pages/ServicesConfigPage'));

// ─── BOOKING ───
const RentalDashboardPage = lazy(() => import('@/platforms/booking/pages/RentalDashboardPage'));
const RentalItemsPage = lazy(() => import('@/platforms/booking/pages/RentalItemsPage'));

// ─── MARKETPLACE ───
const OnlineStoreConfigPage = lazy(() => import('@/platforms/marketplace/pages/OnlineStoreConfigPage'));

// ─── CONTABILIDAD AVANZADA ───
const AdvancedAccountingPage = lazy(() => import('@/pages/AdvancedAccountingPage'));
const FinancialSimulatorsPage = lazy(() => import('@/platforms/tools/pages/FinancialSimulatorsPage'));
const AccountingAuxiliarPage = lazy(() => import('@/platforms/tools/pages/AccountingAuxiliarPage'));
const AccountingPayrollPage = lazy(() => import('@/platforms/tools/pages/AccountingPayrollPage'));
const AccountingCostCentersPage = lazy(() => import('@/platforms/tools/pages/AccountingCostCentersPage'));
const AccountingVouchersPage = lazy(() => import('@/platforms/tools/pages/AccountingVouchersPage'));
const AccountingProductionPage = lazy(() => import('@/platforms/tools/pages/AccountingProductionPage'));
const BankReconciliationView = lazy(() => import('@/platforms/tools/pages/accounting/bank-reconciliation/views/BankReconciliationView'));
const BusinessCandlesticksView = lazy(() => import('@/platforms/tools/pages/accounting/business-candlesticks/views/BusinessCandlesticksView'));
const IndustrialCandlesticksView = lazy(() => import('@/platforms/tools/pages/accounting/industrial-candlesticks/IndustrialCandlesticksView'));
const SponsorRequestPage = lazy(() => import('@/platforms/tools/pages/accounting/industrial-candlesticks/SponsorRequestPage'));
const SponsorRequestsInbox = lazy(() => import('@/platforms/tools/pages/accounting/industrial-candlesticks/SponsorRequestsInbox'));
const AILearningCenter = lazy(() => import('@/lib/local-ai/AILearningCenter'));
const FirmDashboardPage = lazy(() => import('@/platforms/accounting-firm/FirmDashboardPage'));
const FirmClientWindowPage = lazy(() => import('@/platforms/accounting-firm/FirmClientWindowPage'));
const MyPlanPage = lazy(() => import('@/pages/MyPlanPage'));
const AffiliatesPage = lazy(() => import('@/platforms/affiliates/AffiliatesPage'));
const AffiliateWorkspacePage = lazy(() => import('@/platforms/affiliates/AffiliateWorkspacePage'));
const ConferencePage = lazy(() => import('@/platforms/conference/ConferencePage'));
const VerifyNewsPage = lazy(() => import('@/platforms/affiliates/VerifyNewsPage'));
const AccountingApiManagementView = lazy(() => import('@/platforms/tools/pages/accounting/api-access/views/AccountingApiManagementView'));
const AgreementSettlementView = lazy(() => import('@/platforms/tools/pages/agreements/views/AgreementSettlementView'));
const LegalComplianceView = lazy(() => import('@/platforms/tools/pages/legal-compliance/views/LegalComplianceView'));
const SupplierModeDashboardView = lazy(() => import('@/platforms/tools/pages/supplier-mode/views/SupplierModeDashboardView'));
const SubscriptionsView = lazy(() => import('@/platforms/tools/pages/subscriptions/views/SubscriptionsView'));
const ReturnsView = lazy(() => import('@/platforms/tools/pages/returns/views/ReturnsView'));
const BlindCountView = lazy(() => import('@/platforms/tools/pages/blind-count/views/BlindCountView'));
const BranchesView = lazy(() => import('@/platforms/tools/pages/branches/views/BranchesView'));
const AssignmentsView = lazy(() => import('@/platforms/tools/pages/assignments/views/AssignmentsView'));
const DriveView = lazy(() => import('@/platforms/tools/pages/drive/DriveView'));

// ─── MODALES GLOBALES ───
import { NaranjaPlusRequiredModal } from '@/components/ui/NaranjaPlusRequiredModal';
import { TermsGatekeeper } from '@/components/legal/TermsGatekeeper';
import { DbMigrationGuard } from '@/components/ui/DbMigrationGuard';
import { AccountingPlanGate } from '@/components/billing/AccountingPlanGate';
import { StudioPlanGate } from '@/components/billing/StudioPlanGate';
import { PlanGate } from '@/components/billing/PlanGate';
import { VelasGate } from '@/components/billing/VelasGate';

// ─────────────────────────────────────────────────────────────
// 3. LÓGICA DE PROTECCIÓN Y GUARDIA
// ─────────────────────────────────────────────────────────────

const ProtectedLayout = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuthStore();
  const { settings, isLoading: configLoading, loadSettings } = useConfigStore();
  const { checkShiftStatus } = useCashStore();
  const location = useLocation();

  const initSystem = useCallback(async () => {
    try {
      if (user?.role !== 'SUPPLIER') {
        try {
          await db.initializeInvoiceSequence(user?.business_id || 'local');
        } catch (invoiceError) {
          console.warn('Secuencia de facturas no inicializada:', invoiceError);
        }

        await Promise.allSettled([
          loadSettings(),
          checkShiftStatus()
        ]);

        // ☁️ MULTI-DISPOSITIVO: al iniciar sesión, un navegador nuevo recupera
        // catálogo, clientes, ventas, gastos y turnos desde Supabase.
        try {
          const { getLocalSubscriptionState } = await import('@/lib/naranjaPlus');
          const naranjaState = await getLocalSubscriptionState();
          if (naranjaState.isActive) {
            // 🧳 DISPOSITIVO NUEVO: primero se restaura el snapshot COMPLETO.
            // Así no llega la base "por pedacitos": productos, ventas, clientes,
            // gastos, proveedores, configuración y caja llegan de una sola vez.
            try {
              const { restoreSnapshotForNewDevice } = await import('@/lib/cloudSnapshot');
              const snapshotRestore = await restoreSnapshotForNewDevice(
                user?.business_id || 'local',
              );
              if (snapshotRestore.restored) {
                await loadSettings();
                await checkShiftStatus();
                toast.success('Base completa restaurada desde la nube', {
                  description: `${snapshotRestore.inserted ?? 0} registro(s) restaurados de una sola vez.`,
                  duration: 9000,
                });
              }
            } catch (snapshotError) {
              console.warn('Restauración de snapshot omitida:', snapshotError);
            }

            const { hydrateCoreFromCloud } = await import('@/lib/cloudHydrator');
            const hydrated = await hydrateCoreFromCloud(user?.business_id || 'local');
            if (hydrated.imported > 0) {
              await loadSettings();
              await checkShiftStatus();
              toast.success('Datos sincronizados desde la nube', {
                description: `${hydrated.products} producto(s), ${hydrated.customers} cliente(s), ${hydrated.sales} venta(s) recuperados.`,
              });
            }

            // Publica el catálogo/clientes locales para que otro navegador los vea.
            void import('@/lib/cloudHydrator')
              .then(({ syncCoreDataToCloud }) =>
                syncCoreDataToCloud(user?.business_id || 'local'),
              )
              .catch((uploadError: unknown) => {
                console.warn('No se pudo publicar el núcleo local en la nube:', uploadError);
              });

            // Snapshot completo (throttle interno de 10 min) para el próximo equipo.
            void import('@/lib/cloudSnapshot')
              .then(({ uploadSnapshotToCloud }) =>
                uploadSnapshotToCloud(user?.business_id || 'local'),
              )
              .catch((snapshotUploadError: unknown) => {
                console.warn('No se pudo publicar el snapshot completo:', snapshotUploadError);
              });
          }
        } catch (hydrateError) {
          console.warn('Hidratación desde la nube omitida:', hydrateError);
        }

        try {
          const { refreshWallet } = await import('@/lib/wallet');
          await refreshWallet();
          await loadSettings();
          const { flushLocalAiQueue } = await import('@/lib/aiBilling');
          await flushLocalAiQueue();
        } catch (walletError) {
          console.warn('No se pudo refrescar la billetera desde el servidor:', walletError);
        }

        try {
          const { runStartupSelfHealing, dedupeLocalSettings, scrubLegacyPlaintextPasswords } = await import('@/lib/businessIsolation');
          await dedupeLocalSettings();
          await scrubLegacyPlaintextPasswords();
          await runStartupSelfHealing(user?.business_id || 'local');
          await loadSettings();
        } catch (healError) {
          console.warn('Auto-curación de datos locales omitida:', healError);
        }

        const { p2pManager } = await import('@/lib/p2p/P2PManager');
        p2pManager.init(user?.business_id || 'local');
        const { startStoreP2PHost } = await import('@/lib/p2p/StoreP2PServer');
        await startStoreP2PHost(user?.business_id || 'local');
        const { creditBetaMonthly } = await import('@/store/authStore');
        await creditBetaMonthly(user?.email);
      }
    } catch (error) {
      console.error("Error inicializando sistema:", error);
    }
  }, [loadSettings, checkShiftStatus, user]);

  useEffect(() => {
    if (isAuthenticated) {
      initSystem();
    }
  }, [isAuthenticated, initSystem]);

  useRequestPolling(user?.business_id);

  if (authLoading || (isAuthenticated && user?.role !== 'SUPPLIER' && configLoading && !settings)) {
    return <LoadingScreen message="Sincronizando entorno..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role === 'SUPPLIER') {
    return <SupplierLayout />;
  }

  // 📱 En teléfonos solo existe la app móvil, no el ERP de escritorio.
  if (isMobileDevice() && !location.pathname.startsWith('/m')) {
    return <Navigate to="/m/dashboard" replace />;
  }

  return <LayoutMain />;
};

const ModuleGuard = ({ allowedTypes, children }: { allowedTypes: BusinessType[]; children: React.ReactNode }) => {
  const { settings } = useConfigStore();
  const { user } = useAuthStore();
  const isSuperRole = user ? (SUPER_ROLES as readonly string[]).includes(user.role) : false;

  if (!isSuperRole && settings) {
    const currentType = (settings.business_type as BusinessType) || 'RETAIL';
    if (!allowedTypes.includes(currentType)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }
  return <>{children}</>;
};

const RoleGuard = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuthStore();
  const isSuperRole = user ? (SUPER_ROLES as readonly string[]).includes(user.role) : false;

  if (!isSuperRole) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
};

/**
 * Guard por LISTA EXPLÍCITA de roles.
 *
 * A diferencia de `RoleGuard` (que usa SUPER_ROLES = ADMIN + MANAGER), aquí se
 * declara exactamente quién puede entrar. Es el que protege las pantallas
 * sensibles: Configuración Global, finanzas y catálogos.
 *
 * Si el rol no está en la lista, la ruta se bloquea AUNQUE el usuario escriba la
 * URL a mano: no es un ocultamiento del menú, es un bloqueo real de navegación.
 */
const RequireRoles = ({
  allowed,
  children,
}: {
  readonly allowed: readonly string[];
  readonly children: React.ReactNode;
}) => {
  const { user } = useAuthStore();

  if (user === null || !allowed.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
};

/** Roles con acceso a información financiera (gastos, historial, reportes). */
const FINANCE_ACCESS = ['ADMIN', 'MANAGER', 'ACCOUNTANT'] as const;

/** Roles que operan catálogo, clientes y despachos. */
const CATALOG_ACCESS = [
  'ADMIN',
  'MANAGER',
  'CASHIER',
  'RECEPTION',
  'WAREHOUSE_MANAGER',
  'ACCOUNTANT',
] as const;

/**
 * Guard del módulo de Contabilidad Avanzada: rol contable + plan
 * ADVANCED_ACCOUNTING (o superior).
 */
const AccountingRoleGuard = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuthStore();
  const isAuthorized = user
    ? (ACCOUNTING_ROLES as readonly string[]).includes(user.role)
    : false;

  if (!isAuthorized) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <AccountingPlanGate>{children}</AccountingPlanGate>;
};

/** Guard de la firma contable: rol + plan STUDIO_FIRM. */
const StudioGuard = ({ children }: { children: React.ReactNode }) => (
  <AccountingRoleGuard>
    <StudioPlanGate>{children}</StudioPlanGate>
  </AccountingRoleGuard>
);

const SmartHomeRedirect = () => {
  const { settings, loadSettings } = useConfigStore();

  useEffect(() => {
    if (!settings) {
      loadSettings();
    }
  }, [settings, loadSettings]);

  if (isMobileDevice()) {
    return <Navigate to="/m/dashboard" replace />;
  }

  if (!settings) return <LoadingScreen message="Redirigiendo..." />;

  const type = (settings.business_type as BusinessType) || 'RETAIL';

  const redirects: Record<BusinessType, string> = {
    RETAIL: '/platforms/retail/pos',
    RESTAURANT: '/platforms/restaurant/floor',
    PROPERTY: '/platforms/warehouse/rack',
    RENTAL: '/platforms/booking/dashboard',
    HOSPITALITY: '/platforms/hospitality/calendar',
    LOGISTICS: '/platforms/logistics/dashboard',
    ACCOUNTING_FIRM: '/platforms/accounting-firm/dashboard',
  };

  return <Navigate to={redirects[type] || '/platforms/retail/pos'} replace />;
};

const RootRedirect = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const navigate = useNavigate();

  if (authLoading) return <LoadingScreen message="Cargando NIIZ..." />;

  // En teléfonos la puerta de entrada es la app móvil (PIN o correo).
  if (isMobileDevice()) {
    return <Navigate to="/m/dashboard" replace />;
  }

  if (isAuthenticated) {
    return <SmartHomeRedirect />;
  }

  return <LandingPage onSwitchToWelcome={() => navigate('/welcome')} />;
};

const WelcomeRoute = () => {
  const navigate = useNavigate();
  return <WelcomeQuickAccessPage onSwitchToPresentation={() => navigate('/')} />;
};

const StoreRedirect = () => {
  const { user } = useAuthStore();
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    const sessionId = String(user?.business_id || '').trim();
    db.settings.toCollection().first().then(s => {
      const id = sessionId || String(s?.business_id || 'local');
      const slug = String(s?.store_slug || slugifyStore(s?.store_name || s?.business_name || '') || id);
      setTarget(`/s/${slug}`);
    });
  }, [user?.business_id]);
  if (!target) return <LoadingScreen message="Buscando tu tienda..." />;
  return <Navigate to={target} replace />;
};

const ShortStoreRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <Navigate to="/" replace />;
  return <Navigate to={`/tienda/${encodeURIComponent(slug)}`} replace />;
};

// ─────────────────────────────────────────────────────────────
// 4. COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────

function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      void import('@/lib/aiBilling')
        .then((m) => m.flushLocalAiQueue())
        .catch(() => {});
    };
    const handleOffline = () => setIsOnline(false);

    db.initializeTemplates().catch(err =>
      console.warn('[NIIZ] No se pudieron inicializar plantillas:', err)
    );

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true }}>
      <NiizSystemDoctor />

      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500/90 backdrop-blur-sm text-white text-center py-1 z-[9999] flex items-center justify-center gap-2 text-xs font-bold tracking-widest shadow-md transition-all duration-300">
          <WifiOff size={14} />
          <span>MODO OFFLINE ACTIVADO - DATOS LOCALES</span>
        </div>
      )}

      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* ─── RUTAS PÚBLICAS ─── */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/afiliados" element={<AffiliatesPage />} />
          <Route path="/afiliado" element={<AffiliatesPage />} />
          <Route path="/affiliates" element={<AffiliatesPage />} />
          <Route path="/verificar-noticia" element={<VerifyNewsPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/tienda" element={<StoreRedirect />} />
          <Route path="/tienda/:businessId" element={<PublicStorePage />} />
          <Route path="/s/:slug" element={<ShortStoreRedirect />} />
          <Route path="/p/:slug" element={<SupplierPublicPage />} />
          <Route path="/m" element={<MobileAppShell />} />
          <Route path="/m/dashboard" element={<MobileAppShell />} />
          <Route path="/validar/credibilidad/:token" element={<CertificateValidationPage />} />
          <Route path="/proveedor/registro" element={<SupplierRegistration />} />
          <Route path="/cholestupapi" element={<MasterControlPage />} />
        <Route path="/niiz-master-control" element={<Navigate to="/cholestupapi" replace />} />
          <Route path="/niiz-segurity" element={<NiizSegurityPage />} />
          <Route path="/welcome" element={<WelcomeRoute />} />
          <Route path="/rastreo" element={<PublicTrackingPage />} />
          <Route path="/rastreo/:code" element={<PublicTrackingPage />} />
          <Route path="/domiciliario" element={<CourierPortalPage />} />
          <Route path="/domicilio/solicitar" element={<PublicDeliveryRequestPage />} />
          <Route path="/patrocinio-industrial" element={<SponsorRequestPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* ─── RUTAS PROTEGIDAS ─── */}
          <Route element={<ProtectedLayout />}>

            {/* ─── RETAIL ─── */}
            <Route path="platforms/retail">
              <Route index element={<Navigate to="pos" replace />} />
              <Route path="dashboard" element={<RetailDashboardPage />} />
              <Route path="pos" element={<ModuleGuard allowedTypes={['RETAIL']}><RetailPOSPage /></ModuleGuard>} />

              {/* Operación: accesible a quien despacha y atiende. */}
              <Route path="fulfillment" element={<RequireRoles allowed={CATALOG_ACCESS}><FulfillmentDashboardPage /></RequireRoles>} />
              <Route path="supplier/orders" element={<RequireRoles allowed={CATALOG_ACCESS}><SupplierOrdersPage /></RequireRoles>} />

              {/* Catálogo: un mesero o cocinero no lo necesita. */}
              <Route path="productos" element={<RequireRoles allowed={CATALOG_ACCESS}><ProductsPage /></RequireRoles>} />
              <Route path="clientes" element={<RequireRoles allowed={CATALOG_ACCESS}><CustomersPage /></RequireRoles>} />
              <Route path="marketplace" element={<RequireRoles allowed={CATALOG_ACCESS}><MarketplaceRetailPage /></RequireRoles>} />

              {/* FINANZAS: solo roles financieros. Bloqueo real de ruta. */}
              <Route path="gastos" element={<RequireRoles allowed={FINANCE_ACCESS}><ExpensesPage /></RequireRoles>} />
              <Route path="sales-history" element={<RequireRoles allowed={FINANCE_ACCESS}><SalesHistoryPage /></RequireRoles>} />
              <Route path="reportes" element={<RequireRoles allowed={FINANCE_ACCESS}><ReportsPage /></RequireRoles>} />
              <Route path="incentivos" element={<RequireRoles allowed={FINANCE_ACCESS}><IncentivesPage /></RequireRoles>} />

              {/* Gestión de equipo: ADMIN y MANAGER. */}
              <Route path="usuarios" element={<RoleGuard><UsersPage /></RoleGuard>} />

              {/*
               * CONFIGURACIÓN GLOBAL: SOLO el administrador (dueño del negocio).
               * Un gerente NO entra: esta pantalla gobierna negocio, moneda,
               * impuestos, sucursales y planes de toda la plataforma.
               */}
              <Route path="config" element={<RequireRoles allowed={['ADMIN']}><ConfigPage /></RequireRoles>} />
            </Route>

            {/* ─── LOGÍSTICA / DOMICILIOS / TRASTEOS ─── */}
            <Route path="platforms/logistics">
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<LogisticsDashboardPage />} />
            </Route>

            {/* ─── ZANAHORIA STUDIO · FIRMA CONTABLE ─── */}
            <Route path="platforms/accounting-firm">
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<StudioGuard><FirmDashboardPage /></StudioGuard>} />
              <Route path="client/:clientId" element={<StudioGuard><FirmClientWindowPage /></StudioGuard>} />
            </Route>
            <Route path="platforms/tools/verificar-noticia" element={<VerifyNewsPage />} />
            <Route path="platforms/tools/afiliados" element={<AffiliateWorkspacePage />} />
            <Route path="platforms/tools/conferencias" element={<PlanGate required="NARANJA_PLUS" title="Videollamadas" description="Las conferencias P2P forman parte de Naranja Plus y planes superiores."><ConferencePage /></PlanGate>} />
            <Route path="mi-plan" element={<MyPlanPage />} />

            {/* ─── HERRAMIENTAS GLOBALES ─── */}
            <Route path="platforms/tools">
              <Route index element={<Navigate to="documentos" replace />} />
              <Route path="documentos" element={<DocumentsPage />} />
              <Route path="hoja" element={<SheetPage />} />
              <Route path="equipos" element={<EquipmentPage />} />

              {/* 🔒 CONTABILIDAD AVANZADA — dashboard de módulos */}
              <Route
                path="contabilidad"
                element={<AccountingRoleGuard><AdvancedAccountingPage /></AccountingRoleGuard>}
              />

              {/* ✅ Simuladores Financieros */}
              <Route
                path="contabilidad/simuladores"
                element={<AccountingRoleGuard><FinancialSimulatorsPage /></AccountingRoleGuard>}
              />

              {/* ✅ Auxiliar Contable */}
              <Route
                path="contabilidad/auxiliar"
                element={<AccountingRoleGuard><AccountingAuxiliarPage /></AccountingRoleGuard>}
              />

              {/* ✅ Nómina Avanzada */}
              <Route
                path="contabilidad/nomina"
                element={<AccountingRoleGuard><AccountingPayrollPage /></AccountingRoleGuard>}
              />

              {/* ✅ Centros de Costos */}
              <Route path="contabilidad/costos" element={<AccountingRoleGuard><AccountingCostCentersPage /></AccountingRoleGuard>} />

              {/* ✅ Comprobantes Oficiales */}
              <Route path="contabilidad/comprobantes" element={<AccountingRoleGuard><AccountingVouchersPage /></AccountingRoleGuard>} />

              {/* ✅ Ensamblaje y Producción */}
              <Route path="contabilidad/produccion" element={<AccountingRoleGuard><AccountingProductionPage /></AccountingRoleGuard>} />

              {/* ✅ Conciliación Bancaria */}
              <Route path="contabilidad/conciliacion" element={<AccountingRoleGuard><BankReconciliationView /></AccountingRoleGuard>} />

              {/* ✅ Velas Japonesas del Negocio (OHLCV local) */}
              <Route path="velas-negocio" element={<AccountingRoleGuard><BusinessCandlesticksView /></AccountingRoleGuard>} />
              <Route path="velas-industriales" element={<AccountingRoleGuard><VelasGate><IndustrialCandlesticksView /></VelasGate></AccountingRoleGuard>} />
              <Route path="patrocinios" element={<AccountingRoleGuard><SponsorRequestsInbox /></AccountingRoleGuard>} />
              <Route path="aprendizaje-ia" element={<AccountingRoleGuard><AILearningCenter /></AccountingRoleGuard>} />

              {/* ✅ Acceso API para Contador Externo */}
              <Route path="contabilidad/acceso-api" element={<AccountingRoleGuard><AccountingApiManagementView /></AccountingRoleGuard>} />

              {/* ✅ Convenios / Créditos financiados / Terceros pagadores */}
              <Route path="contabilidad/convenios" element={<AccountingRoleGuard><AgreementSettlementView /></AccountingRoleGuard>} />

              {/* ✅ Bóveda Legal / Pólizas / Compliance */}
              <Route path="contabilidad/cumplimiento-legal" element={<AccountingRoleGuard><LegalComplianceView /></AccountingRoleGuard>} />

              {/* ✅ Modo Proveedor Mayorista nativo (B2B) */}
              <Route path="proveedor" element={<RoleGuard><SupplierModeDashboardView /></RoleGuard>} />
              <Route path="suscripciones" element={<RoleGuard><SubscriptionsView /></RoleGuard>} />
              <Route path="devoluciones" element={<RoleGuard><ReturnsView /></RoleGuard>} />
              <Route path="conteo-ciego" element={<RoleGuard><BlindCountView /></RoleGuard>} />
              <Route path="sucursales" element={<RoleGuard><BranchesView /></RoleGuard>} />
              <Route path="asignaciones" element={<AssignmentsView />} />
              <Route path="drive" element={<DriveView />} />
            </Route>

            {/* ─── RESTAURANT ─── */}
            <Route path="platforms/restaurant">
              <Route index element={<Navigate to="floor" replace />} />
              <Route path="dashboard" element={<Navigate to="floor" replace />} />
              <Route path="floor" element={<ModuleGuard allowedTypes={['RESTAURANT']}><TableMapPage /></ModuleGuard>} />
              <Route path="order/:tableId" element={<ModuleGuard allowedTypes={['RESTAURANT']}><RestaurantOrderPage /></ModuleGuard>} />
              <Route path="kds" element={<ModuleGuard allowedTypes={['RESTAURANT']}><KDSPage /></ModuleGuard>} />
              <Route path="bar" element={<ModuleGuard allowedTypes={['RESTAURANT']}><BarMonitor /></ModuleGuard>} />
            </Route>

            {/* ─── WAREHOUSE ─── */}
            <Route path="platforms/warehouse">
              <Route index element={<Navigate to="rack" replace />} />
              <Route path="rack" element={<ModuleGuard allowedTypes={['PROPERTY']}><PropertyGridPage /></ModuleGuard>} />
              <Route path="units" element={<RoleGuard><ModuleGuard allowedTypes={['PROPERTY']}><UnitsConfigPage /></ModuleGuard></RoleGuard>} />
            </Route>

            {/* ─── HOSPITALITY ─── */}
            <Route path="platforms/hospitality">
              <Route index element={<Navigate to="calendar" replace />} />
              <Route path="calendar" element={<ModuleGuard allowedTypes={['HOSPITALITY']}><BookingPage /></ModuleGuard>} />
              <Route path="services" element={<RoleGuard><ModuleGuard allowedTypes={['HOSPITALITY']}><ServicesConfigPage /></ModuleGuard></RoleGuard>} />
            </Route>

            {/* ─── BOOKING ─── */}
            <Route path="platforms/booking">
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<ModuleGuard allowedTypes={['RENTAL']}><RentalDashboardPage /></ModuleGuard>} />
              <Route path="items" element={<ModuleGuard allowedTypes={['RENTAL']}><RentalItemsPage /></ModuleGuard>} />
            </Route>

            {/* ─── MARKETPLACE ─── */}
            <Route path="platforms/marketplace">
              <Route index element={<Navigate to="config" replace />} />
              <Route path="config" element={<RoleGuard><OnlineStoreConfigPage /></RoleGuard>} />
            </Route>

            {/* ─── SUPPLIER B2B ─── */}
            <Route path="platforms/supplier">
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<SupplierDashboard />} />
              <Route path="catalog" element={<SupplierCatalog />} />
              <Route path="config" element={<SupplierConfig />} />
              <Route path="templates" element={<SupplierTemplates />} />
              <Route path="analysis" element={<SupplierAnalysisPage />} />
              <Route path="subastas" element={<SupplierSubastasPage />} />
            </Route>

          </Route>
          {/* ↑ Cierre correcto de ProtectedLayout */}

          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Suspense>

      <NaranjaPlusRequiredModal />
      <TermsGatekeeper />
      <DbMigrationGuard />
    </BrowserRouter>
  );
}

export default App;